import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useT } from '../../utils/useTranslation';
import { DB_TYPE } from '../../api';
import { db } from '../../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { motion } from 'framer-motion';
import HeroBackground from '../../components/ui/HeroBackground';

export default function BoardMemberDetail() {
  const { id } = useParams();
  const t = useT();
  const isRTL = t.language === 'sindhi';
  const [boardMember, setBoardMember] = useState(null);
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMember = async () => {
      setLoading(true);
      if (DB_TYPE === 'firebase') {
        try {
          const docRef = doc(db, 'office_bearers', id);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            const data = { _id: docSnap.id, ...docSnap.data() };
            setBoardMember(data);
            
            if (data.member_id) {
              const mRef = doc(db, 'members', data.member_id);
              const mSnap = await getDoc(mRef);
              if (mSnap.exists()) setMember({ _id: mSnap.id, ...mSnap.data() });
            }
          }
        } catch (err) {
          console.error(err);
        } finally {
          setLoading(false);
        }
      }
    };
    fetchMember();
  }, [id]);

  if (loading) return <div style={{ padding: 120, display: 'flex', justifyContent: 'center' }}><div className="spinner" /></div>;
  if (!boardMember || !member) return <div style={{ padding: 120, textAlign: 'center' }}>Member not found.</div>;

  const displayName = isRTL ? (member.full_name_sindhi || member.full_name) : member.full_name;
  const displayRole = isRTL ? (t(boardMember.role.toLowerCase().replace(/ /g, '')) || boardMember.role) : boardMember.role;

  return (
    <>
      <div className="hero-premium" style={{ minHeight: '400px', padding: '120px 2rem 80px', direction: isRTL ? 'rtl' : 'ltr' }}>
        <HeroBackground />
        <div style={{ position: 'relative', zIndex: 10, textAlign: isRTL ? 'right' : 'center', maxWidth: 1000, margin: '0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Link to="/board" style={{ color: 'var(--sindhi-gold)', textDecoration: 'none', marginBottom: 20, fontWeight: 700, alignSelf: isRTL ? 'flex-end' : 'flex-start' }}>
            {isRTL ? '← پوئتي باڊي ڏانهن' : '← Back to Board'}
          </Link>
          <div style={{
            width: 160,
            height: 160,
            borderRadius: '50%',
            background: 'var(--sindhi-ivory)',
            marginBottom: 24,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 64,
            border: '4px solid var(--sindhi-gold)',
            overflow: 'hidden',
            boxShadow: '0 15px 35px -10px rgba(0,0,0,0.3)'
          }}>
            {member.photo ? <img src={member.photo} alt={displayName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : '👤'}
          </div>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-gradient-gold" style={{
            fontSize: isRTL ? 56 : 48,
            fontWeight: 800,
            marginBottom: 8,
            letterSpacing: -1,
            fontFamily: isRTL ? 'var(--font-sindhi)' : 'var(--font-display)'
          }}>
            {displayName}
          </motion.h1>
          <div style={{
            fontSize: 20,
            fontWeight: 800,
            color: 'white',
            textTransform: 'uppercase',
            letterSpacing: 2,
            marginBottom: 12,
            fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit',
            background: 'rgba(255,255,255,0.1)',
            padding: '6px 20px',
            borderRadius: 100,
            border: '1px solid rgba(255,255,255,0.2)'
          }}>
            {displayRole} ({boardMember.level})
          </div>
          <div style={{ fontSize: 16, color: 'rgba(255,255,255,0.8)', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>
            📅 {boardMember.tenure_year}
          </div>
        </div>
      </div>

      <div style={{ background: 'var(--sindhi-ivory)', padding: '60px 24px', minHeight: '50vh' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto', display: 'grid', gap: 32, gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', direction: isRTL ? 'rtl' : 'ltr' }}>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} style={{ background: 'white', borderRadius: 24, padding: 40, border: '1px solid var(--sindhi-cream)', boxShadow: '0 10px 30px -10px rgba(0,0,0,0.05)' }}>
            <h3 style={{ fontSize: 24, fontWeight: 800, color: 'var(--sindhi-navy)', marginBottom: 24, fontFamily: isRTL ? 'var(--font-sindhi)' : 'var(--font-display)', display: 'flex', alignItems: 'center', gap: 12 }}>
              <span>📝</span> {isRTL ? 'ذاتي تفصيل' : 'Personal Details'}
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, fontSize: 15 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--sindhi-cream)', paddingBottom: 8 }}>
                <span style={{ color: 'var(--sindhi-text-muted)', fontWeight: 600 }}>{isRTL ? 'ادبي نالو' : 'Literary Name'}</span>
                <span style={{ fontWeight: 800, color: 'var(--sindhi-navy)' }}>{member.literary_name || '—'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--sindhi-cream)', paddingBottom: 8 }}>
                <span style={{ color: 'var(--sindhi-text-muted)', fontWeight: 600 }}>{isRTL ? 'رڪنيت جي حيثيت' : 'Membership Status'}</span>
                <span style={{ fontWeight: 800, color: 'var(--sindhi-navy)' }}>{member.membership_status || '—'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--sindhi-cream)', paddingBottom: 8 }}>
                <span style={{ color: 'var(--sindhi-text-muted)', fontWeight: 600 }}>{isRTL ? 'رڪنيت جي تاريخ' : 'Join Date'}</span>
                <span style={{ fontWeight: 800, color: 'var(--sindhi-navy)' }}>{member.join_date ? member.join_date.split('T')[0] : '—'}</span>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }} style={{ background: 'white', borderRadius: 24, padding: 40, border: '1px solid var(--sindhi-cream)', boxShadow: '0 10px 30px -10px rgba(0,0,0,0.05)' }}>
            <h3 style={{ fontSize: 24, fontWeight: 800, color: 'var(--sindhi-navy)', marginBottom: 24, fontFamily: isRTL ? 'var(--font-sindhi)' : 'var(--font-display)', display: 'flex', alignItems: 'center', gap: 12 }}>
              <span>📚</span> {isRTL ? 'ادبي خدمتون' : 'Literary Services'}
            </h3>
            <div style={{ fontSize: 15, color: 'var(--sindhi-text)', lineHeight: 1.8, fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit', whiteSpace: 'pre-line' }}>
              {member.literary_services || (isRTL ? 'ڪوبه تفصيل موجود ناهي.' : 'No details available.')}
            </div>
            
            {member.literary_genre && member.literary_genre.length > 0 && (
              <div style={{ marginTop: 24, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {member.literary_genre.map((g, i) => (
                  <span key={i} style={{ background: 'var(--sindhi-ivory)', color: 'var(--sindhi-navy)', padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700, fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit', border: '1px solid var(--sindhi-cream)' }}>
                    {g}
                  </span>
                ))}
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </>
  );
}
